import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { Card, Container } from "react-bootstrap";
import { toast } from "react-toastify";
import OrderItem from "../../api/models/addOnTesting/ShoppingCart";
import { APIError } from "../../api/ObservableFromFetch";
import { addToCart, getAvailableTests, getCart, getOrdered, removeFromCart } from "../../api/ShoppingCart";
import { useTranslationWithRef } from "../../hooks/useTranslationWithRef";
import { CartStep } from "./CartStep";
import { ConsentStep } from "./ConsentStep";
import { Header } from "./Header";
import { PanelDetail } from "./PanelDetail";
import { ShoppingCartStep } from "./ShoppingCartStep";
import { TestsAvailable } from "./TestsAvailable";

interface CartProps {
  testEventId: number
}

const Cart = (props: CartProps) => {
  const [translationRef, t] = useTranslationWithRef()
  const [invalidate, setInvalidate] = useState<number>(0)
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    const subscription = getAvailableTests(props.testEventId).subscribe({
      next: (results: OrderItem[]) => setItems(results),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])

  useEffect(() => {
    const subscription = getCart(props.testEventId).subscribe({
      next: (results: OrderItem[]) => {
        setCartItems(results)
        setProcessing(false)
      },
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])

  useEffect(() => {
    const subscription = getOrdered(props.testEventId).subscribe({
      next: (results: OrderItem[]) => setPreviousOrders(results),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, translationRef])

  const [items, setItems] = useState<OrderItem[]>([])          // available
  const [cartItems, setCartItems] = useState<OrderItem[]>([])  // in cart
  const [previousOrders, setPreviousOrders] = useState<OrderItem[]>([])  // previous orders
  const [displayedPanel, setDisplayedPanel] = useState<OrderItem | null>(null)

  const [prevStep, setPrevStep] = useState<CartStep>(CartStep.Unknown)
  const [currentStep, setCurrentStep] = useState<CartStep>(CartStep.TestsAvailable)

  const showPanelDetailsPage = (item: OrderItem) => {
    setDisplayedPanel(item)
    setPrevStep(CartStep.TestsAvailable)
    setCurrentStep(CartStep.PanelDetails)
  }

  const goToShoppingCart = () => {
    setPrevStep(CartStep.TestsAvailable)
    setCurrentStep(CartStep.ShoppingCart)
  }

  const goToTestsAvailable = () => {
    setPrevStep(CartStep.Unknown)
    setCurrentStep(CartStep.TestsAvailable)
  }

  const goToConsent = () => {
    setPrevStep(CartStep.ShoppingCart)
    setCurrentStep(CartStep.Consent)
  }

  const showCartDetailsPage = (item: OrderItem) => {
    setDisplayedPanel(item)
    setPrevStep(CartStep.ShoppingCart)
    setCurrentStep(CartStep.PanelDetails)
  }

  const addCart = (values: number[]) => {
    setProcessing(true)
    setItems(items => items.filter(i => !values.includes(i.addOnTestingPanelId ?? 0)))

    addToCart({ id: props.testEventId, ids: values }).subscribe({
      next: () => {
        setInvalidate(prev => prev + 1)
      },
      error: (e: APIError) => {
        setInvalidate(prev => prev + 1)
        toast.error(t(e.message, { data: e.data }))
      }
    })
  }

  const removeCart = (item: OrderItem) => {
    setProcessing(true)
    setCartItems(items => items.filter(i => i.addOnTestingPanelId !== item.addOnTestingPanelId))

    removeFromCart(item.addOnTestingOrderItemId!, props.testEventId).subscribe({
      next: () => {
        setInvalidate(prev => prev + 1)
        if (cartItems.length === 1) {
          goToTestsAvailable()
        }
      },
      error: (e: APIError) => {
        setInvalidate(prev => prev + 1)
        toast.error(t(e.message, { data: e.data }))
      }
    })
  }

  return (
    <Container className="mt-5">
      <Header count={cartItems.length} step={currentStep} />

      <Card className="shadow-lg rounded-lg border-0">
        <Card.Body className="p-4">
          {currentStep === CartStep.PanelDetails && displayedPanel && <PanelDetail panel={displayedPanel}
            onAdd={prevStep !== CartStep.ShoppingCart ? () => {
              addCart([displayedPanel.addOnTestingPanelId ?? 0])
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.Unknown)
            } : undefined}
            onRemove={prevStep === CartStep.ShoppingCart ? () => {
              removeCart(displayedPanel)
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.TestsAvailable)
            } : undefined}
            onClose={() => {
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.Unknown)
            }} />}
          {currentStep === CartStep.TestsAvailable &&
            <TestsAvailable items={items} cartItems={cartItems} previousOrders={previousOrders} processing={processing}
              onExpand={showPanelDetailsPage} onAddCart={addCart} onNext={goToShoppingCart} />}

          {currentStep === CartStep.ShoppingCart &&
            <ShoppingCartStep items={cartItems} onExpand={showCartDetailsPage} processing={processing}
              onRemove={removeCart} onBack={goToTestsAvailable} onNext={goToConsent} />
          }

          {currentStep === CartStep.Consent && <div hidden={currentStep !== CartStep.Consent}>
            <ConsentStep cartItems={cartItems} testEventId={props.testEventId}
              onBack={() => {
                setPrevStep(CartStep.TestsAvailable)
                setCurrentStep(CartStep.ShoppingCart)
              }}
              onSubmit={() => {
                // TODO: FIXME
                //setPrevStep(CartStep.TestsAvailable)
                //setCurrentStep(CartStep.ShoppingCart)
              }} />
          </div>}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default observer(Cart)
